/** What the user is told about a failed second-factor request. */
export type MfaErrorKey =
  | 'mfaCodeInvalid'
  | 'mfaCodeUsed'
  | 'mfaChallengeExpired'
  | 'mfaMethodRequired'
  | 'mfaSetupExpired'
  | 'mfaWrongPassword'
  | 'mfaLocked'
  | 'mfaCodeRecentlySent'
  | 'errorTryAgain';

/**
 * A failed second-factor request, explained by a translation key: the API's
 * messages are English. Keeps the status, which the global server-error
 * notification reads.
 */
export class MfaError extends Error {
  constructor(
    readonly key: MfaErrorKey,
    readonly status?: number,
  ) {
    super(key);
    this.name = 'MfaError';
  }
}

/** The statuses of toMfaResponseError in the API. */
function errorKeyForStatus(status: number): MfaErrorKey {
  switch (status) {
    case 400:
      return 'mfaWrongPassword';
    case 401:
      return 'mfaCodeInvalid';
    case 403:
      return 'mfaChallengeExpired';
    case 409:
      return 'mfaMethodRequired';
    case 422:
      return 'mfaSetupExpired';
    case 410:
      return 'mfaCodeUsed';
    case 423:
      return 'mfaLocked';
    case 429:
      return 'mfaCodeRecentlySent';
    default:
      return 'errorTryAgain';
  }
}

/** For a raw fetch, whose failed response has nothing but the status. */
export function mfaErrorFromStatus(status: number): MfaError {
  return new MfaError(errorKeyForStatus(status), status);
}

/** Without a status, the request never got an answer: a network error, left as it is. */
export function toMfaError(error: unknown): unknown {
  if (error instanceof MfaError) return error;
  const { status } = (error ?? {}) as { status?: unknown };
  return typeof status === 'number' ? mfaErrorFromStatus(status) : error;
}

/** The key to show for any error a second-factor request may throw. */
export function mfaErrorKey(error: unknown): MfaErrorKey {
  const mapped = toMfaError(error);
  return mapped instanceof MfaError ? mapped.key : 'errorTryAgain';
}
